import { useState, useCallback } from 'react';

interface TgImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  fallbackSrc?: string;
  /** Use lazy loading (default: true). Set to false for first visible / LCP images. */
  lazy?: boolean;
  /** Preload the full image into browser cache before displaying. Use for fullscreen view. */
  preload?: boolean;
}

/**
 * WebKit-hardened image component for Telegram Mini App.
 *
 * Handles:
 * - lazy / eager loading
 * - async decoding to avoid jank
 * - automatic 1-shot retry with cache-busting on load error
 * - security assertion for leaked s3:// URIs
 */
export function TgImage({
  src,
  fallbackSrc,
  lazy = true,
  preload = false,
  alt = '',
  style,
  ...rest
}: TgImageProps) {
  const [imgSrc, setImgSrc] = useState<string>(() => {
    if (src && src.startsWith('s3://')) {
      console.error('[SECURITY] frontend received raw storage URI:', src);
    }
    return src;
  });
  const [retried, setRetried] = useState(false);

  // Preload full-res into browser cache (for fullscreen view)
  if (preload && src) {
    const prefetchImg = new window.Image();
    prefetchImg.src = src;
  }

  const handleError = useCallback(() => {
    if (!retried) {
      // One-shot retry with cache-busting param
      const sep = imgSrc.includes('?') ? '&' : '?';
      const bustedUrl = `${imgSrc}${sep}t=${Date.now()}`;
      console.warn('[TgImage] load failed, retrying with cache-bust:', bustedUrl);
      setImgSrc(bustedUrl);
      setRetried(true);
    } else if (fallbackSrc) {
      // After retry failure, show fallback
      setImgSrc(fallbackSrc);
    }
  }, [imgSrc, retried, fallbackSrc]);

  return (
    <img
      {...rest}
      src={imgSrc}
      alt={alt}
      loading={lazy ? 'lazy' : 'eager'}
      decoding="async"
      style={style}
      onError={handleError}
    />
  );
}
